import type { ReactNode, SVGProps } from "react";

/** Shared stroke icons — wi-gold/wi-text, 1.5px stroke (PRD §3 / About). */
const strokeProps = {
	fill: "none",
	stroke: "currentColor",
	strokeWidth: 1.5,
	strokeLinecap: "round" as const,
	strokeLinejoin: "round" as const,
};

type IconProps = SVGProps<SVGSVGElement> & { title?: string };

function IconShell({ title, children, className = "", ...rest }: IconProps & { children: ReactNode }) {
	return (
		<svg
			viewBox="0 0 24 24"
			className={["h-6 w-6 shrink-0 text-wi-gold", className].filter(Boolean).join(" ")}
			aria-hidden={title ? undefined : true}
			role={title ? "img" : undefined}
			{...rest}
		>
			{title ? <title>{title}</title> : null}
			{children}
		</svg>
	);
}

export function IconGather(props: IconProps) {
	return (
		<IconShell title={props.title ?? "Gather ideas"} {...props}>
			<circle cx="8" cy="10" r="2.5" {...strokeProps} />
			<circle cx="16" cy="8" r="2.5" {...strokeProps} />
			<circle cx="14" cy="16" r="2.5" {...strokeProps} />
			<path d="M10 11.5 13.2 9.2M10.2 12.2 12.5 14.5" {...strokeProps} />
		</IconShell>
	);
}

export function IconVote(props: IconProps) {
	return (
		<IconShell title={props.title ?? "Vote fairly"} {...props}>
			<path d="M6 18V9M10 18V6M14 18v-7M18 18V8" {...strokeProps} />
		</IconShell>
	);
}

export function IconDecide(props: IconProps) {
	return (
		<IconShell title={props.title ?? "Decide on record"} {...props}>
			<path d="M5 12.5 9.5 17 19 7.5" {...strokeProps} />
		</IconShell>
	);
}

export function IconShield(props: IconProps) {
	return (
		<IconShell title={props.title ?? "Privacy"} {...props}>
			<path d="M12 3.5 19 6.5v5c0 4.2-2.9 7.4-7 8.5-4.1-1.1-7-4.3-7-8.5v-5Z" {...strokeProps} />
			<path d="M9.5 12.2 11.4 14l3.6-4" {...strokeProps} />
		</IconShell>
	);
}

export function IconLink(props: IconProps) {
	return (
		<IconShell title={props.title ?? "Curator link"} {...props}>
			<path d="M9.5 14.5a3.5 3.5 0 0 1 0-5l2-2a3.5 3.5 0 0 1 5 5l-1 1" {...strokeProps} />
			<path d="M14.5 9.5a3.5 3.5 0 0 1 0 5l-2 2a3.5 3.5 0 1 1-5-5l1-1" {...strokeProps} />
		</IconShell>
	);
}

export function IconClock(props: IconProps) {
	return (
		<IconShell title={props.title ?? "Expires on schedule"} {...props}>
			<circle cx="12" cy="12" r="8" {...strokeProps} />
			<path d="M12 8v4.5l3 1.5" {...strokeProps} />
		</IconShell>
	);
}

export function IconMath(props: IconProps) {
	return (
		<IconShell title={props.title ?? "Transparent math"} {...props}>
			<path d="M7 8h10M7 16h10M9.5 5.5 7 12l2.5 6.5M14.5 5.5 17 12l-2.5 6.5" {...strokeProps} />
		</IconShell>
	);
}

/** Diagram: preference ring — ordered slots around a center (About §problem). */
export function DiagramConsensusRing({ className = "" }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 260 200"
			className={["mx-auto h-auto w-full max-w-sm text-wi-gold", className].filter(Boolean).join(" ")}
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<title>Consensus ring — ordered preference</title>
			<circle cx="130" cy="100" r="78" opacity="0.2" />
			<path d="M 130 22 A 78 78 0 0 1 202 56" opacity="0.85" />
			<path d="M 202 56 A 78 78 0 0 1 208 124" opacity="0.55" />
			<path d="M 208 124 A 78 78 0 0 1 162 174" opacity="0.4" />
			<path d="M 162 174 A 78 78 0 0 1 74 158" opacity="0.25" strokeDasharray="3 4" />
			<circle cx="130" cy="100" r="6" fill="var(--color-wi-surface)" stroke="currentColor" strokeWidth="1.5" />
			{[0, 72, 144, 216, 288].map((deg, i) => {
				const r = (deg * Math.PI) / 180;
				const x = 130 + Math.cos(r) * 52;
				const y = 100 + Math.sin(r) * 52;
				return <circle key={deg} cx={x} cy={y} r="3.2" opacity={0.35 + i * 0.12} />;
			})}
		</svg>
	);
}

/** Diagram: inputs → rules → outcome (About §ethics). */
export function DiagramFlowLattice({ className = "" }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 380 156"
			className={["mx-auto h-auto w-full max-w-xl text-wi-gold", className].filter(Boolean).join(" ")}
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<title>From discussion fragments to one defensible outcome</title>
			<defs>
				<marker id="wi-flow-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
					<polygon points="0 0, 7 3.5, 0 7" className="fill-current" stroke="none" />
				</marker>
			</defs>
			<rect x="14" y="32" width="92" height="92" rx="12" opacity="0.35" />
			<path d="M34 60 H72 M38 78 H76 M42 96 H82 M30 112 H68" opacity="0.5" />
			<text x="60" y="24" className="fill-wi-text font-geist text-[10px] font-medium" textAnchor="middle" stroke="none">
				Inputs
			</text>
			<path d="M 106 78 H 152" markerEnd="url(#wi-flow-arrow)" />
			<rect x="158" y="38" width="72" height="80" rx="14" />
			<text x="194" y="64" className="fill-wi-text font-geist text-[10px] font-medium" textAnchor="middle" stroke="none">
				Rules
			</text>
			<path d="M 178 88 L 194 76 L 210 88 M 194 76 V 98" opacity="0.55" />
			<path d="M 230 78 H 276" markerEnd="url(#wi-flow-arrow)" />
			<rect x="282" y="32" width="88" height="92" rx="12" />
			<text x="326" y="24" className="fill-wi-text font-geist text-[10px] font-medium" textAnchor="middle" stroke="none">
				Outcome
			</text>
			<path d="M 302 72 L 318 60 L 334 72 M 310 90 L 334 90 M 306 108 L 330 108" opacity="0.45" />
		</svg>
	);
}
